import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import type { MenuNode } from '../api/types';
import { Icon } from './Icon';

/**
 * Le menu, tel que `/api/me` le rend — filtré, élagué, traduit.
 *
 * CE COMPOSANT NE DÉCIDE RIEN. Il n'interroge aucune permission, ne masque
 * aucune entrée, ne replie aucune section vide : tout cela a déjà eu lieu côté
 * serveur. Refaire le filtrage ici donnerait deux règles pour une décision, et la
 * plus laxiste finirait par gagner sans que personne ne le voie.
 *
 * Ce qu'il sait faire : distinguer une SECTION (route nulle) d'une entrée, et
 * s'afficher RÉDUIT. Réduit, le libellé reste dans le lien — masqué à l'œil, lu
 * par le lecteur d'écran — et réapparaît en infobulle (`data-tip`) au survol et
 * au focus. L'icône vient de `MenuSpec::icon`, déclaré dans le manifeste.
 */
/** Toutes les routes de l'arbre, pour départager les préfixes (`/relations` et `/relations/organisations`). */
function routesOf(nodes: readonly MenuNode[]): string[] {
    return nodes.flatMap((node) => [...(node.route ?? null) === null ? [] : [node.route as string], ...routesOf(node.children)]);
}

/**
 * Une entrée est active quand l'adresse est la sienne ou en descend, sauf si une
 * autre entrée du menu correspond plus précisément : sur la fiche d'une
 * organisation, « Organisations » s'allume, pas « Tableau de bord » (`/relations`).
 */
function matches(path: string, route: string): boolean {
    return path === route || path.startsWith(route.endsWith('/') ? route : `${route}/`);
}

export function MenuTree({
    nodes,
    depth = 0,
    collapsed = false,
    all,
}: {
    readonly nodes: readonly MenuNode[];
    readonly depth?: number;
    readonly collapsed?: boolean;
    readonly all?: readonly string[];
}): ReactNode {
    const { pathname } = useLocation();
    const routes = all ?? routesOf(nodes);
    const best = routes.filter((r) => matches(pathname, r)).sort((a, b) => b.length - a.length)[0] ?? null;

    return (
        <ul className="menu" data-depth={depth}>
            {nodes.map((node) => {
                // `?? null` et non `=== null` : un serveur reconfiguré qui omettrait
                // la clé ne doit pas transformer une section en lien (voir MePayloadTest).
                const route = node.route ?? null;

                return (
                    <li key={node.code} className="menu__item">
                        {route === null ? (
                            collapsed ? (
                                <hr className="menu__divider" aria-hidden="true" />
                            ) : (
                                <span className="menu__section">{node.label}</span>
                            )
                        ) : (
                            <NavLink
                                to={route}
                                end={false}
                                data-tip={collapsed ? node.label : undefined}
                                className={() => (route === best ? 'menu__link menu__link--active' : 'menu__link')}
                                aria-current={route === best ? 'page' : undefined}
                            >
                                <Icon name={node.icon ?? 'dot'} size={20} />
                                <span className={collapsed ? 'visually-hidden' : 'menu__label'}>{node.label}</span>
                            </NavLink>
                        )}

                        {node.children.length > 0 ? <MenuTree nodes={node.children} depth={depth + 1} collapsed={collapsed} all={routes} /> : null}
                    </li>
                );
            })}
        </ul>
    );
}
